/**
 * WeaveIn.ai Platform - Public Links Management Page
 * View and manage all public form tokens across the organization.
 */

import React, { useState, useEffect } from 'react';
import { Globe, Link, Copy, CheckCircle, ExternalLink, Trash2, Loader2, Plus, QrCode } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import PublicLinkQRCode from '@/components/forms/PublicLinkQRCode';
import { useTabbedChrome } from '@/components/weavein';

interface PublicToken {
  id: number;
  token: string;
  form_template_id: number;
  template_name?: string;
  title?: string;
  is_active: boolean;
  expires_at?: string;
  max_submissions?: number;
  current_submissions: number;
  create_contact_on_submit?: boolean;
  program_id?: number;
  public_url: string;
  created_at?: string;
}

export default function PublicLinksPage() {
  const tabbed = useTabbedChrome();
  const [tokens, setTokens] = useState<PublicToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const loadTokens = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/forms/public-tokens');
      setTokens(Array.isArray(res.data) ? res.data : []);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadTokens(); }, []);

  const handleDeactivate = async (id: number) => {
    if (!confirm('Deactivate this public link? It will no longer accept submissions.')) return;
    try { await apiClient.delete(`/api/forms/public-tokens/${id}`); await loadTokens(); } catch {}
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getFullUrl = (token: string) => `${baseUrl}/public/forms/${token}`;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-600" />
              Public Links
            </h1>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Manage public form links shared via URL, QR code, or embedded on external websites. Create links from the Form Builder &gt; Share button.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /> Loading...</div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Globe className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <h3 className="text-lg font-semibold text-gray-600 mb-1">No Public Links</h3>
          <p className="text-sm">Create public links from the Form Builder by clicking "Share" on any form template.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tokens.map((t) => (
            <div key={t.id} className={`p-4 bg-white dark:bg-gray-800 border rounded-lg ${t.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-gray-100 opacity-60'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{t.title || t.template_name || 'Untitled'}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Form: {t.template_name || `#${t.form_template_id}`}
                    {t.created_at && <> &middot; Created {new Date(t.created_at).toLocaleDateString()}</>}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="text-right">
                    <div className="font-semibold text-gray-900 dark:text-gray-100">{t.current_submissions}</div>
                    <div className="text-xs text-gray-400">submissions</div>
                  </div>
                  {t.max_submissions && (
                    <div className="text-right">
                      <div className="text-xs text-gray-400">of {t.max_submissions} max</div>
                    </div>
                  )}
                </div>
              </div>

              {t.is_active && (
                <div className="space-y-2">
                  {/* URL */}
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      readOnly
                      value={getFullUrl(t.token)}
                      className="flex-1 text-xs text-gray-500 bg-gray-50 dark:bg-gray-900 px-3 py-1.5 rounded border border-gray-200 dark:border-gray-600 font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(getFullUrl(t.token), `url-${t.id}`)}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {copied === `url-${t.id}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                      Copy
                    </button>
                    <a
                      href={getFullUrl(t.token)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-blue-600"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Open
                    </a>
                  </div>

                  {/* QR code: generate, download, share, copy, print */}
                  <PublicLinkQRCode linkId={t.id} title={t.title || t.template_name || 'Form'} />

                  {/* Embed code */}
                  <div className="flex items-center gap-2">
                    <QrCode className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <input
                      readOnly
                      value={`<iframe src="${getFullUrl(t.token)}" width="100%" height="800" frameborder="0"></iframe>`}
                      className="flex-1 text-xs text-gray-500 bg-gray-50 dark:bg-gray-900 px-3 py-1.5 rounded border border-gray-200 dark:border-gray-600 font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(`<iframe src="${getFullUrl(t.token)}" width="100%" height="800" frameborder="0"></iframe>`, `embed-${t.id}`)}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {copied === `embed-${t.id}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                      Embed
                    </button>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {t.create_contact_on_submit && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">Auto-creates contacts</span>}
                    {t.expires_at && <span>Expires: {new Date(t.expires_at).toLocaleDateString()}</span>}
                    <button
                      onClick={() => handleDeactivate(t.id)}
                      className="flex items-center gap-1 text-red-500 hover:underline ml-auto"
                    >
                      <Trash2 className="w-3 h-3" /> Deactivate
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
